import React, {useEffect, useMemo} from 'react';
import {MapContainer, Marker, Tooltip, useMap} from 'react-leaflet';
import L, {divIcon} from 'leaflet';
import '../../../../node_modules/leaflet/dist/leaflet.css';
import CustomTileLayer from "../layout/map/CustomTileLayer.js";
import {useGetTraceJourneeQuery} from "../../../modules/simulations/simulations.api";
import {marqueursEquipe, positionsCadrage} from "./simulationRules";
import {traceDeLEquipe} from "./atelierRules";
import TraceJournee from "./TraceJournee";
import {morceauxTrace} from "./traceRules";
import "./carte.css";

// Couleurs d'équipe : tokens existants de DESIGN.md §1 (navy, vert maison, brun, orange, violet) — aucun hex nouveau.
const COULEURS_EQUIPE = ['#213067', '#7bb421', '#B1560F', '#F39A3F', '#9d23d1'];
const couleur = (equipe) => COULEURS_EQUIPE[(equipe - 1) % COULEURS_EQUIPE.length];

function iconNumero(numero, equipe) {
    return divIcon({
        className: 'simulation-marker',
        html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;background:${couleur(equipe)};color:#fff;font-weight:700;font-size:12px;border:2px solid #fff">${numero}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
    });
}

function iconPoint(lettre) {
    return divIcon({
        className: 'simulation-marker',
        html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:4px;background:#fff;color:#213067;font-weight:800;font-size:12px;border:2px solid #213067">${lettre}</span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
    });
}

function Ajuste({positions}) {
    const map = useMap();
    useEffect(() => {
        if (positions.length === 1) map.setView(positions[0], 14);
        else if (positions.length > 1) map.fitBounds(L.latLngBounds(positions), {padding: [40, 40]});
    }, [map, positions]);
    return null;
}

/**
 * Carte d'une journée : marqueurs numérotés dans l'ordre de l'API (U-5), tracé routier base → visites → retour quand l'API le
 * fournit (lot 8), sinon lignes droites, base (B) et dépôt (D) marqués. `equipes` : numéros d'équipe qui travaillent ce jour-là.
 */
function SimulationJourneeMap({simulationId, date, points, equipes, trajets = {}, base, depot}) {
    // currentData : jamais le tracé de la journée précédente sur les marqueurs de la nouvelle.
    const {currentData: donneesTrace} = useGetTraceJourneeQuery({id: simulationId, date}, {skip: !simulationId || !date});
    const couches = useMemo(() => equipes.map((e) => {
        const marqueurs = marqueursEquipe(points, e);
        const route = traceDeLEquipe(donneesTrace, e);
        const origine = [depot, base].find((p) => p && p.lat != null && p.lon != null);
        const o = origine && trajets[e] ? [origine.lat, origine.lon] : null;
        const calque = {points: marqueurs, trace: marqueurs.map((m) => m.position), depart: o, retour: o, aTrajet: Boolean(o) && marqueurs.length > 0};
        const routier = route?.routier && route.positions.length > 1;
        return {equipe: e, marqueurs, morceaux: morceauxTrace({route: routier ? route.positions : null, calque})};
    }), [points, equipes, trajets, base, depot, donneesTrace]);

    const positions = useMemo(() => positionsCadrage(points, equipes), [points, equipes]);

    return (
        <MapContainer center={[46.6, 2.4]} zoom={6} style={{height: '60vh', width: '100%'}}>
            <CustomTileLayer maxZoom={19} className="tuiles-estompees"/>
            <Ajuste positions={positions}/>
            {couches.map((c) => (
                <React.Fragment key={c.equipe}>
                    <TraceJournee morceaux={c.morceaux} couleur={couleur(c.equipe)}/>
                    {c.marqueurs.map((m) => (
                        <Marker key={`${c.equipe}-${m.numero}`} position={m.position} icon={iconNumero(m.numero, c.equipe)}>
                            <Tooltip>{m.numero}. {m.nomenclature}</Tooltip>
                        </Marker>
                    ))}
                </React.Fragment>
            ))}
            {Object.values(trajets).some(Boolean) && base?.lat != null && <Marker position={[base.lat, base.lon]} icon={iconPoint('B')}><Tooltip>Base de départ</Tooltip></Marker>}
            {Object.values(trajets).some(Boolean) && depot?.lat != null && <Marker position={[depot.lat, depot.lon]} icon={iconPoint('D')}><Tooltip>Dépôt local</Tooltip></Marker>}
        </MapContainer>
    );
}

export default SimulationJourneeMap;
